// @ts-strict-ignore
import React, { useState } from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  Button,
  BodyText,
  HeaderText,
  RadioButton,
  Stack,
  Separator,
} from '../../../ui';
import { MultiFactorAuthMethod, MultiFactorAuthChoiceType } from './types';
import { ChattingCommentIcon, MailIcon } from '../../../ui/Icons';
import { LIVE_CHAT_SUPPORT_PAGE_URL } from '../../../../modules/SupportServiceWrapper';
import { openExternalUrl } from '../../../../modules/openExternalUrl';

export interface IMultiFactorAuthMethodFormProps {
  mfaChoices: MultiFactorAuthChoiceType[];
  onSubmit?: (selectedMfaMethod: MultiFactorAuthMethod) => void;
}

export const TRANSACTIONAL_COPY = 'By requesting a multi-factor authentication code, you agree to be contacted by StyleSeat via SMS.';

const MFA_OPTIONS = {
  [MultiFactorAuthMethod.Email]: {
    COPY_METHOD: null,
    ICON_METHOD: () => <MailIcon height={20} width={20} />,
  },
  [MultiFactorAuthMethod.SMS]: {
    COPY_METHOD: TRANSACTIONAL_COPY,
    ICON_METHOD: () => <ChattingCommentIcon />,
  },
};

// Given a list of MFA delivery choices, return the first
const pickFirstMfaChoice = ([firstChoice]: MultiFactorAuthChoiceType[]) => (
  firstChoice?.length ? firstChoice[0] : null
);

const handleContactSupportClick = () => {
  openExternalUrl(LIVE_CHAT_SUPPORT_PAGE_URL, '_blank');
};

const MultiFactorAuthMethodForm: React.FCWithChildren<IMultiFactorAuthMethodFormProps> = ({
  mfaChoices,
  onSubmit,
}) => {
  const [
    selectedMfaMethod,
    setSelectedMfaMethod,
  ] = useState<MultiFactorAuthMethod | null>(pickFirstMfaChoice(mfaChoices));

  const handleSendCode = () => {
    onSubmit(selectedMfaMethod);
  };

  return (
    <Stack space={16} testID="multi-factor-auth-type-form">
      <HeaderText level={1}>
        Two-factor authentication required
      </HeaderText>
      <BodyText color={BrandColor.Metal}>
        Any time you log in from an unrecognized device, we&apos;ll ask for extra security
      </BodyText>
      <Box paddingTop={24}>
        <HeaderText level={3}>We&apos;ll send a code to</HeaderText>
      </Box>
      <Box>
        {(mfaChoices).map(([choice, deliverTo], idx) => (
          <React.Fragment key={choice}>
            <Box
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              paddingX={2}
            >
              <Box flexGrow={0.1}>
                {MFA_OPTIONS[choice].ICON_METHOD && MFA_OPTIONS[choice].ICON_METHOD()}
              </Box>
              <Box flexGrow={0.8}>
                <HeaderText level={3}>{MultiFactorAuthMethod[choice]}</HeaderText>
                <BodyText color={BrandColor.Metal}>{deliverTo}</BodyText>
              </Box>
              <Box flexGrow={0.1} alignItems="flex-end" style={{ height: '100%' }}>
                <RadioButton
                  isSelected={selectedMfaMethod === choice}
                  onToggle={() => setSelectedMfaMethod(choice)}
                  testID={`mfa-${MultiFactorAuthMethod[choice].toLowerCase()}-option`}
                  padding={12}
                  width={18}
                  height={18}
                  borderWidth={5}
                />
              </Box>
            </Box>
            { MFA_OPTIONS[choice].COPY_METHOD && (
              <Box
                style={{
                  backgroundColor: BrandColor.LightGrey,
                  marginTop: 14,
                  marginBottom: 4,
                }}
                paddingX={16}
                paddingY={10}
                testID="multi-factor-transactional-copy"
                borderRadius={7}
              >
                <BodyText color={BrandColor.Metal} weight="thin" appearance="medium">
                  { MFA_OPTIONS[choice].COPY_METHOD }
                </BodyText>
              </Box>
            )}
            {idx < mfaChoices.length - 1 && (
              <Separator appearance="extrathin" color={BrandColor.LightGrey} paddingY={12} />
            )}
          </React.Fragment>
        ))}
      </Box>
      <Box paddingTop={24}>
        <Button
          appearance="primary-neue"
          disabled={!selectedMfaMethod}
          onClick={handleSendCode}
        >
          Send Code
        </Button>
      </Box>
      <Inline paddingTop={8} justifyContent="space-around">
        <Button
          appearance="link-lowercase"
          size="medium"
          onClick={handleContactSupportClick}
          style={{ textDecoration: 'none' }}
          clickTracking="client_loginpage_contactsupport_clicked"
        >
          Contact Support
        </Button>
      </Inline>
    </Stack>
  );
};

MultiFactorAuthMethodForm.defaultProps = {
  onSubmit: () => { },
};

export default MultiFactorAuthMethodForm;
